<script setup lang="ts">
    import { useWidgetFormStore } from '@/js/stores/widgetForm';
    import { useLibraryStore } from '@/js/stores/library';
    import { computed, onMounted, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import { __, __untypedExpression } from '../../stores/expressions';
    import ToggleButton from '../ToggleButton.vue';
    import { ChartColumnIcon, CheckIcon, PlusIcon, TypeIcon } from '@lucide/vue';
    import GenericForm from '../widgetForm/GenericForm.vue';
    import GraphForm from '../widgetForm/GraphForm.vue';
    import TextWidgetForm from '../widgetForm/textWidgetForm.vue';
    import WidgetFormPreview from '../widgetForm/WidgetFormPreview.vue';
    import FilterFormModal from '../FilterFormModal/FilterFormModal.vue';
    import { FilterStoreName } from '@/js/services/api/filter/types';

    type WidgetType = {
        //id: IWidgetType;
        id: 'Graph' | 'Text';
        translationString: string;
    };

    const widgetTypes: WidgetType[] = [
        {
            id: 'Graph',
            translationString: 'widget-type-graph',
        },
        // {
        //     id: 'Prompt',
        //     label: 'Prompt',
        //     icon: "chat.svg"
        // },
        //     {
        //     id: 'TopList',
        //     label: 'Toplista',
        //     icon: "title.svg"
        // },
        {
            id: 'Text',
            translationString: 'widget-type-free-text',
        },
    ];

    const props = defineProps<{ show: boolean }>();

    const emit = defineEmits<{
        (e: 'close', dashboardId?: string): void;
    }>();

    const route = useRoute();
    const widgetFormStore = useWidgetFormStore();
    const libraryStore = useLibraryStore();

    const selectedWidgetType = computed(() => widgetFormStore.widget.type);

    const onOpen = async () => {
        if (!route.query.id) return;

        if (route.path === '/workspace/dashboard') {
            if (route.query.widgetForm === 'new') {
                widgetFormStore.initNewWidgetForm();
            } else if (route.query.widgetForm) {
                await widgetFormStore.initEditWidgetForm(
                    route.query.id.toString(),
                    route.query.widgetForm.toString()
                );
            }
        } else if (route.path === '/workspace/library') {
            if (route.query.widgetForm) {
                if (libraryStore.widgets.length === 0) {
                    await libraryStore.getWidgets();
                }
                const widgetId = route.query.widgetForm.toString();
                const widget = libraryStore.widgets.find((w) => w.id === widgetId);
                if (widget) {
                    widgetFormStore.initCopyLibraryWidgetForm(widget);
                }
            }
        }

        if (!widgetFormStore.kpi) {
            await widgetFormStore.fetchKpiOtions();
        }
    };

    onMounted(onOpen);
    watch(
        () => props.show,
        async (open) => {
            if (open && !widgetFormStore.originalWidgetData) {
                onOpen();
            }
        }
    );

    const submitWidget = async (open?: boolean) => {
        if (!route.query.id || !route.query.widgetForm) {
            return;
        }
        if (widgetFormStore.mode === 'edit') {
            await widgetFormStore.editWidget(
                route.query.id.toString(),
                route.query.widgetForm?.toString()
            );
        } else {
            await widgetFormStore.createNewWidget(route.query.id.toString());
        }

        closeModal(open);
    };

    const closeModal = (open?: boolean) => {
        emit('close', open ? route.query.id?.toString() : undefined);
        widgetFormStore.resetWidget();
    };
</script>

<template>
    <FilterFormModal
        :show="show"
        :filter-store-name="FilterStoreName.WidgetFilter"
        :loading="widgetFormStore.formLoading && show"
        :valid="widgetFormStore.formIsValid"
        :show-filters="selectedWidgetType === 'Graph'"
        :submit-text="
            widgetFormStore.mode === 'edit' ? __('edit-widget-save') : __('widget-form-add-widget')
        "
        :submit-and-open="widgetFormStore.mode === 'copyLibrary'"
        :submit-and-open-text="__('add-widget-open')"
        @close="closeModal"
        @submit="submitWidget"
    >
        <template #submit-icon>
            <CheckIcon v-if="widgetFormStore.mode === 'edit'" />
            <PlusIcon v-else />
        </template>
        <template #form>
            <h3 class="heading-md">
                {{
                    widgetFormStore.mode === 'create'
                        ? __('create-widget-title')
                        : __('edit-widget-title')
                }}
            </h3>
            <p class="text-body py-0.5">
                {{ __('create-widget-description') }}
            </p>
            <div v-if="widgetFormStore.mode === 'create'" class="flex gap-1.5 mt-5">
                <ToggleButton
                    v-for="type in widgetTypes"
                    :id="type.id"
                    :key="type.id"
                    :aria-label="__untypedExpression(type.translationString)"
                    :selected="selectedWidgetType === type.id"
                    :on-click="() => widgetFormStore.setSelectedWidgetType(type.id)"
                    :label="__untypedExpression(type.translationString)"
                >
                    <template #icon>
                        <ChartColumnIcon v-if="type.id === 'Graph'" class="size-5" />
                        <TypeIcon v-else-if="type.id === 'Text'" class="size-5" />
                    </template>
                </ToggleButton>
            </div>
            <div class="mt-10 font-medium">
                <div v-if="selectedWidgetType === 'Graph'">
                    <GraphForm />
                </div>
                <div v-else-if="selectedWidgetType === 'Text'">
                    <TextWidgetForm />
                </div>
                <div v-else>
                    <GenericForm />
                </div>
            </div>
        </template>
        <template #preview>
            <WidgetFormPreview />
        </template>
    </FilterFormModal>
</template>
